import { queryOptions, type QueryClient } from '@tanstack/react-query';
import type { Benchmarks, NetworkUsage } from '../../data/analytics';
import { platformKey, platformKeyForRoute } from '../../lib/catalogPlatform';
import { fetchBenchmarks, fetchNetworkUsage } from './repository';
import { benchmarksQueryOptions, networkUsageQueryOptions, NETWORK_STALE_TIME } from './queries';

export type PageMetricScope = { platform: string } | { sku: string };

const containsSku = (scope: PageMetricScope, sku: string): boolean =>
  'sku' in scope ? sku === scope.sku : platformKey(sku) === platformKeyForRoute(scope.platform);

/** Detail pages read only SKU traffic, not the network's totals or daily chart.
 * A distinct key keeps this projection out of the full network cache. Without
 * a scope the dashboard retains the complete response under its existing key. */
export const pageNetworkUsageQueryOptions = (queryClient: QueryClient, scope?: PageMetricScope) => {
  const full = networkUsageQueryOptions();
  const project = (data: NetworkUsage): Pick<NetworkUsage, 'byApi'> => ({
    byApi: data.byApi.filter((row) => !scope || containsSku(scope, row.sku)),
  });
  return queryOptions<Pick<NetworkUsage, 'byApi'>>({
    queryKey: scope ? [...full.queryKey, scope] as const : full.queryKey,
    queryFn: async () => {
      const data = await fetchNetworkUsage();
      return scope ? project(data) : data;
    },
    initialData: () => {
      const data = queryClient.getQueryData(full.queryKey);
      return data && scope ? project(data) : undefined;
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(full.queryKey)?.dataUpdatedAt,
    staleTime: NETWORK_STALE_TIME,
  });
};

/** Keep the gateway's window, definition and provenance alongside the rows:
 * the FAQ and reference table use the same measurement and verified date. */
export const pageBenchmarksQueryOptions = (queryClient: QueryClient, scope: PageMetricScope) => {
  const full = benchmarksQueryOptions();
  const project = (data: Benchmarks): Benchmarks => ({
    ...data,
    bySku: data.bySku.filter((row) => containsSku(scope, row.sku)),
  });
  return queryOptions<Benchmarks>({
    queryKey: [...full.queryKey, scope] as const,
    queryFn: async () => project(await fetchBenchmarks()),
    initialData: () => {
      const data = queryClient.getQueryData(full.queryKey);
      return data ? project(data) : undefined;
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(full.queryKey)?.dataUpdatedAt,
    staleTime: NETWORK_STALE_TIME,
  });
};

/** As with full-network measurements, a metrics outage must not replace a
 * complete catalog page with an error boundary. */
export const loadPageMeasuredFigures = (queryClient: QueryClient, scope: PageMetricScope) =>
  Promise.all([
    queryClient.ensureQueryData(pageBenchmarksQueryOptions(queryClient, scope)).catch(() => undefined),
    queryClient.ensureQueryData(pageNetworkUsageQueryOptions(queryClient, scope)).catch(() => undefined),
  ]);
